import { QueryClient } from '@tanstack/react-query';

export const CacheDurations = {
  GENRE_STATS: 30 * 60 * 1000,
  USER_VIDEOS: 60 * 60 * 1000,
  CHANNEL_METADATA: 12 * 60 * 60 * 1000,
  CHANNEL_VIDEOS: 6 * 60 * 60 * 1000,
  AUDIO_FEATURES: 24 * 60 * 60 * 1000,
  SEARCH_RESULTS: 15 * 60 * 1000,
  USER_PREFERENCES: 7 * 24 * 60 * 60 * 1000,
} as const;

const defaultQueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: (failureCount: number, error: any) => {
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: false as const,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
};

export function createQueryClient(): QueryClient {
  return new QueryClient(defaultQueryClientConfig);
}

export const queryKeys = {
  users: ['users'] as const,
  user: (userId: string) => [...queryKeys.users, userId] as const,
  userVideos: (userId: string) => [...queryKeys.user(userId), 'videos'] as const,
  userPreferences: (userId: string) => [...queryKeys.user(userId), 'preferences'] as const,

  channels: ['channels'] as const,
  channel: (channelId: string) => [...queryKeys.channels, channelId] as const,
  channelVideos: (channelId: string) => [...queryKeys.channel(channelId), 'videos'] as const,
  channelMetadata: (channelId: string) => [...queryKeys.channel(channelId), 'metadata'] as const,
  followedChannels: ['channels', 'followed'] as const,

  videos: ['videos'] as const,
  video: (videoId: string) => [...queryKeys.videos, videoId] as const,
  videoAudioFeatures: (videoId: string) => [...queryKeys.video(videoId), 'audio-features'] as const,
  similarVideos: (videoId: string) => [...queryKeys.video(videoId), 'similar'] as const,

  search: ['search'] as const,
  searchResults: (query: string, filters?: any) => [...queryKeys.search, 'results', query, filters] as const,
  searchSuggestions: (query: string) => [...queryKeys.search, 'suggestions', query] as const,
  searchAutocomplete: (query: string) => [...queryKeys.search, 'autocomplete', query] as const,
  popularSearches: ['search', 'popular'] as const,
  trendingContent: ['search', 'trending'] as const,

  audioFeatures: ['audio-features'] as const,
  audioFeaturesByBpm: (bpm: number, range?: number) => [...queryKeys.audioFeatures, 'bpm', bpm, range] as const,
  audioFeaturesByKey: (key: string) => [...queryKeys.audioFeatures, 'key', key] as const,
  audioFeaturesStats: ['audio-features', 'stats'] as const,

  genres: ['genres'] as const,
  genreStats: ['genres', 'stats'] as const,
  genreContent: (genre: string) => [...queryKeys.genres, genre] as const,

  suggestions: ['suggestions'] as const,

  channelPreview: (channelId: string) => ['channelPreview', channelId] as const,
} as const;

export const cacheInvalidation = {
  invalidateUser: (queryClient: QueryClient, userId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
  },

  invalidateChannel: (queryClient: QueryClient, channelId: string) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.channel(channelId) });
  },

  invalidateSearch: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.search });
  },

  invalidateAudioFeatures: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.audioFeatures });
  },

  clearAll: (queryClient: QueryClient) => {
    queryClient.clear();
  },
} as const;

export const localStorageCache = {
  set: <T>(key: string, value: T, ttl?: number): void => {
    try {
      const item = {
        value,
        timestamp: Date.now(),
        ttl: ttl || CacheDurations.USER_PREFERENCES,
      };
      localStorage.setItem(key, JSON.stringify(item));
    } catch (error) {
      console.warn('Failed to set local storage cache:', error);
    }
  },

  get: <T>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const parsed = JSON.parse(item);
      const now = Date.now();

      if (parsed.ttl && (now - parsed.timestamp) > parsed.ttl) {
        localStorage.removeItem(key);
        return null;
      }

      return parsed.value;
    } catch (error) {
      console.warn('Failed to get local storage cache:', error);
      return null;
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove local storage cache:', error);
    }
  },

  clear: (): void => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('meradio:')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear local storage cache:', error);
    }
  },
};

export const localStorageKeys = {
  userPreferences: 'meradio:user:preferences',
  searchHistory: 'meradio:search:history',
  playlistState: 'meradio:playlist:state',
  audioSettings: 'meradio:audio:settings',
  themeSettings: 'meradio:theme:settings',
} as const;
